import type { Middleware } from '@reduxjs/toolkit';
import { baseApi } from '../api/base-api';
import { meApi } from '../api/me-api';
import { modelsApi } from '../api/models-api';
import { datastreamsApi } from '../api/datastreams-api';
import { conversationsApi } from '../api/conversations-api';
import { advanceIdentityScope, getIdentityGeneration, getIdentityScopeKey, IDENTITY_CHANGED, IDENTITY_CLEAR } from '../utils/identity-scope';

function projectedAccessChanged(previous: unknown, projection: unknown, paths: string[]): boolean {
  const record = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
  return paths.some((path) => {
    let oldValue = previous,
      newValue = projection;
    const keys = path.split('.');
    for (const key of keys.slice(0, -1)) {
      if (!record(oldValue) || !record(newValue)) return false;
      oldValue = oldValue[key];
      newValue = newValue[key];
    }
    const field = keys.at(-1)!;
    return record(oldValue) && record(newValue) && Object.hasOwn(oldValue, field) !== Object.hasOwn(newValue, field);
  });
}

export const identityMiddleware: Middleware = (store) => {
  const requestGenerations = new Map<string, number>();
  const deniedReads = new Set<string>();
  let observedScope = '';

  const reset = (scope: string, clearUser: boolean) => {
    if (scope !== observedScope || clearUser) deniedReads.clear();
    observedScope = scope;
    const generation = advanceIdentityScope(scope);
    store.dispatch({ type: IDENTITY_CHANGED, payload: { scope, generation, clearUser } });
    store.dispatch(baseApi.util.resetApiState());
  };

  return (next) => (unknownAction) => {
    const action = unknownAction as { type?: string; meta?: { requestId?: string; arg?: { endpointName?: string; type?: string; originalArgs?: unknown } }; payload?: any };
    const endpoint = action.meta?.arg?.endpointName;
    const requestId = action.meta?.requestId;
    if (requestId && action.type?.endsWith('/pending')) requestGenerations.set(requestId, getIdentityGeneration());
    if (requestId && (action.type?.endsWith('/fulfilled') || action.type?.endsWith('/rejected'))) {
      const startedGeneration = requestGenerations.get(requestId);
      requestGenerations.delete(requestId);
      if (startedGeneration !== undefined && startedGeneration !== getIdentityGeneration()) return unknownAction;
    }

    if (action.type === IDENTITY_CLEAR) {
      reset('', true);
      return unknownAction;
    }

    const resourceId = action.meta?.arg?.originalArgs;
    const fulfilled = action.type?.endsWith('/fulfilled');
    let projectionChanged = false;
    if (fulfilled && typeof resourceId === 'string') {
      if (endpoint === 'getModelById') projectionChanged = projectedAccessChanged(modelsApi.endpoints.getModelById.select(resourceId)(store.getState()).data?.response, action.payload?.response, ['id', 'llm', 'prompts', 'mcp_servers', 'voice_model', 'published_voice_model_id', 'voice.status', 'display.powered_by_karla_icon']);
      if (endpoint === 'getDatastreamById') projectionChanged = projectedAccessChanged(datastreamsApi.endpoints.getDatastreamById.select(resourceId)(store.getState()).data?.response, action.payload?.response, ['id', 'state', 'data_collected', 'data_indexes', 'recovery', 'train_attempts_count']);
      if (endpoint === 'getConversationById') projectionChanged = projectedAccessChanged(conversationsApi.endpoints.getConversationById.select(resourceId)(store.getState()).data?.response, action.payload?.response, ['id', 'usage', 'logs', 'end_user', 'inbox.account_id']);
    }
    const result = next(unknownAction);
    const readKey = action.meta?.arg?.type === 'query' ? JSON.stringify([endpoint, action.meta.arg.originalArgs]) : null;
    if (readKey && action.type?.endsWith('/fulfilled')) deniedReads.delete(readKey);
    if (projectionChanged && observedScope && typeof resourceId === 'string') {
      // A full projection can reveal changed read policy without a role-name change.
      // Reset editors and other resource caches, then retain this authoritative response.
      const identityResponse = meApi.endpoints.getMe.select(undefined)(store.getState()).data;
      reset(observedScope, false);
      if (identityResponse) store.dispatch(meApi.util.upsertQueryEntries([{ endpointName: 'getMe', arg: undefined, value: identityResponse }]));
      if (endpoint === 'getModelById') store.dispatch(modelsApi.util.upsertQueryEntries([{ endpointName: 'getModelById', arg: resourceId, value: action.payload }]));
      if (endpoint === 'getDatastreamById') store.dispatch(datastreamsApi.util.upsertQueryEntries([{ endpointName: 'getDatastreamById', arg: resourceId, value: action.payload }]));
      if (endpoint === 'getConversationById') store.dispatch(conversationsApi.util.upsertQueryEntries([{ endpointName: 'getConversationById', arg: resourceId, value: action.payload }]));
      next(unknownAction);
    } else if (endpoint === 'getMe' && action.type?.endsWith('/fulfilled')) {
      const scope = getIdentityScopeKey(action.payload?.response?.user);
      if (scope !== observedScope) {
        reset(scope, !scope);
        store.dispatch(meApi.util.upsertQueryEntries([{ endpointName: 'getMe', arg: undefined, value: action.payload }]));
      }
    } else if (endpoint === 'reauth' && action.type?.endsWith('/fulfilled')) {
      reset('', true);
      try {
        window.localStorage.setItem('karla:identity-event', String(Date.now()));
      } catch {
        // The local reset still applies when browser storage is unavailable.
      }
    } else if (action.type?.endsWith('/rejected') && action.payload?.status === 401 && observedScope) {
      reset('', true);
    } else if (readKey && action.type?.endsWith('/rejected') && action.payload?.status === 403 && observedScope && !deniedReads.has(readKey)) {
      // A read denial can reveal a narrowed profile even when the role name is unchanged.
      // Reset once per denied resource; the next denial must reach the UI without a retry loop.
      deniedReads.add(readKey);
      const identityResponse = meApi.endpoints.getMe.select(undefined)(store.getState()).data;
      reset(observedScope, false);
      if (identityResponse) store.dispatch(meApi.util.upsertQueryEntries([{ endpointName: 'getMe', arg: undefined, value: identityResponse }]));
    }
    return result;
  };
};
